import React from "react";

const RatingGauge = ({
  qualityRating = 90,
  serviceRating = 80,
  priceRating = 20,
  size = 300,
}) => {
  // Single dial stroke width
  const dialStroke = size * 0.15;

  // Convert rating (0-100) to angle (0 to 360 degrees, starting from right)
  const getAngle = (rating) => {
    return (rating / 100) * 360;
  };

  // Convert polar coordinates to cartesian
  const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    };
  };

  // Create SVG arc path
  const createArcPath = (centerX, centerY, radius, startAngle, endAngle) => {
    const start = polarToCartesian(centerX, centerY, radius, endAngle);
    const end = polarToCartesian(centerX, centerY, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    return [
      "M",
      start.x,
      start.y,
      "A",
      radius,
      radius,
      0,
      largeArcFlag,
      0,
      end.x,
      end.y,
    ].join(" ");
  };

  const centerX = size / 2;
  const centerY = size / 2;
  const baseRadius = size * 0.38;
  const needleRadius = baseRadius;

  // Scale other elements (moved up before usage)
  const needleStrokeWidth = size * 0.033;
  const iconSize = size * 0.1;
  const iconRadius = size * 0.075;
  const iconStrokeWidth = size * 0.02;
  const centerHubRadius = size * 0.042;
  const centerDotRadius = size * 0.02;
  const shadowOffset = size * 0.017;

  // Calculate needle positions for the three dials
  const qualityAngle = getAngle(qualityRating);
  const serviceAngle = getAngle(serviceRating);
  const priceAngle = getAngle(priceRating);

  // Define needles with their properties
  const needles = [
    {
      id: "quality",
      rating: qualityRating,
      angle: qualityAngle,
      color: "#7b189f",
      label: "Quality",
      icon: "star",
    },
    {
      id: "service",
      rating: serviceRating,
      angle: serviceAngle,
      color: "#7b189f",
      label: "Service",
      icon: "user",
    },
    {
      id: "price",
      rating: priceRating,
      angle: priceAngle,
      color: "#7b189f",
      label: "Price",
      icon: "dollar",
    },
  ];

  // Detect overlapping needles and adjust positions
  const stackThreshold = 5; // Degrees within which needles are considered overlapping

  // Group needles by proximity and calculate their groups
  const overlapGroups = [];
  const processed = new Set();

  needles.forEach((needle, index) => {
    if (processed.has(index)) return;

    const group = [index];
    processed.add(index);

    needles.forEach((n, i) => {
      if (i === index || processed.has(i)) return;
      const angleDiff = Math.abs(n.angle - needle.angle);
      const normalizedDiff = Math.min(angleDiff, 360 - angleDiff);
      if (normalizedDiff <= stackThreshold) {
        group.push(i);
        processed.add(i);
      }
    });

    overlapGroups.push(group);
  });

  // Calculate arranged needles with adaptive sizing
  const arrangedNeedles = needles.map((needle, index) => {
    // Find which group this needle belongs to
    const groupIndex = overlapGroups.findIndex((g) => g.includes(index));
    const group = overlapGroups[groupIndex];
    const groupSize = group.length;
    const positionInGroup = group.indexOf(index);

    // Adaptive icon sizing based on group size
    let scaleFactor = 1;
    let stackOffset = iconRadius * 2.5;

    if (groupSize === 2) {
      scaleFactor = 0.85; // Slightly smaller for 2 overlapping
      stackOffset = iconRadius * 2.5 * scaleFactor;
    } else if (groupSize === 3) {
      scaleFactor = 0.7; // Smaller for all 3 overlapping
      stackOffset = iconRadius * 2.5 * scaleFactor;
    }

    // Calculate radius adjustment (stack inward)
    const radiusAdjustment = -positionInGroup * stackOffset;
    const adjustedRadius = needleRadius + radiusAdjustment;
    const position = polarToCartesian(
      centerX,
      centerY,
      adjustedRadius,
      needle.angle
    );

    return {
      ...needle,
      position,
      adjustedRadius,
      scaleFactor,
      zIndex: groupSize > 1 ? index : 0,
    };
  });

  // Icon rendering functions
  const renderStarIcon = (x, y, color, scale = 1) => (
    <g
      transform={`translate(${x - (iconSize * scale) / 2}, ${
        y - (iconSize * scale) / 2
      })`}
    >
      <circle
        cx={(iconSize * scale) / 2}
        cy={(iconSize * scale) / 2}
        r={iconRadius * scale}
        fill="white"
        stroke={color}
        strokeWidth={iconStrokeWidth * scale}
      />
      <path
        d={`M${(iconSize * scale) / 2} ${(iconSize * scale) / 6}l${
          (iconSize * scale) / 12
        } ${(iconSize * scale) / 4}L${(iconSize * scale * 5) / 6} ${
          (iconSize * scale * 5) / 12
        }l-${(iconSize * scale) / 6} ${(iconSize * scale) / 6}L${
          (iconSize * scale * 7) / 10
        } ${(iconSize * scale * 5) / 6}l-${(iconSize * scale) / 5}-${
          (iconSize * scale) / 10
        }L${(iconSize * scale * 3) / 10} ${(iconSize * scale * 5) / 6}l${
          (iconSize * scale) / 30
        }-${(iconSize * scale) / 4}l-${(iconSize * scale) / 6}-${
          (iconSize * scale) / 6
        }L${(iconSize * scale * 5) / 12} ${(iconSize * scale * 5) / 12}L${
          (iconSize * scale) / 2
        } ${(iconSize * scale) / 6}z`}
        fill={color}
      />
    </g>
  );

  const renderUserIcon = (x, y, color, scale = 1) => (
    <g
      transform={`translate(${x - (iconSize * scale) / 2}, ${
        y - (iconSize * scale) / 2
      })`}
    >
      <circle
        cx={(iconSize * scale) / 2}
        cy={(iconSize * scale) / 2}
        r={iconRadius * scale}
        fill="white"
        stroke={color}
        strokeWidth={iconStrokeWidth * scale}
      />
      <circle
        cx={(iconSize * scale) / 2}
        cy={(iconSize * scale * 5) / 12}
        r={(iconSize * scale * 2) / 15}
        fill="none"
        stroke={color}
        strokeWidth={iconStrokeWidth * scale * 0.67}
      />
      <path
        d={`M${(iconSize * scale) / 4} ${(iconSize * scale * 3) / 4}c0-${
          (iconSize * scale * 2) / 15
        } ${(iconSize * scale * 7) / 60}-${(iconSize * scale) / 5} ${
          (iconSize * scale) / 4
        }-${(iconSize * scale) / 5}s${(iconSize * scale) / 4} ${
          (iconSize * scale) / 15
        } ${(iconSize * scale) / 4} ${(iconSize * scale) / 5}`}
        fill="none"
        stroke={color}
        strokeWidth={iconStrokeWidth * scale * 0.67}
        strokeLinecap="round"
      />
    </g>
  );

  const renderDollarIcon = (x, y, color, scale = 1) => (
    <g
      transform={`translate(${x - (iconSize * scale) / 2}, ${
        y - (iconSize * scale) / 2
      })`}
    >
      <circle
        cx={(iconSize * scale) / 2}
        cy={(iconSize * scale) / 2}
        r={iconRadius * scale}
        fill="white"
        stroke={color}
        strokeWidth={iconStrokeWidth * scale}
      />
      <line
        x1={(iconSize * scale) / 2}
        y1={(iconSize * scale) / 6}
        x2={(iconSize * scale) / 2}
        y2={(iconSize * scale * 5) / 6}
        stroke={color}
        strokeWidth={iconStrokeWidth * scale * 0.67}
      />
      <path
        d={`M${(iconSize * scale * 11) / 30} ${(iconSize * scale * 11) / 30}A${
          (iconSize * scale) / 10
        } ${(iconSize * scale) / 10} 0 0 1 ${(iconSize * scale * 14) / 30} ${
          (iconSize * scale * 3) / 10
        }h${(iconSize * scale * 2) / 15}A${(iconSize * scale) / 10} ${
          (iconSize * scale) / 10
        } 0 0 1 ${(iconSize * scale * 19) / 30} ${
          (iconSize * scale * 2) / 5
        }v0A${(iconSize * scale) / 10} ${(iconSize * scale) / 10} 0 0 1 ${
          (iconSize * scale * 16) / 30
        } ${(iconSize * scale) / 2}h-${(iconSize * scale * 2) / 15}A${
          (iconSize * scale) / 10
        } ${(iconSize * scale) / 10} 0 0 0 ${(iconSize * scale * 11) / 30} ${
          (iconSize * scale * 3) / 5
        }v0A${(iconSize * scale) / 10} ${(iconSize * scale) / 10} 0 0 0 ${
          (iconSize * scale * 14) / 30
        } ${(iconSize * scale * 7) / 10}h${(iconSize * scale * 2) / 15}A${
          (iconSize * scale) / 10
        } ${(iconSize * scale) / 10} 0 0 0 ${(iconSize * scale * 19) / 30} ${
          (iconSize * scale * 3) / 5
        }`}
        fill="none"
        stroke={color}
        strokeWidth={iconStrokeWidth * scale * 0.67}
        strokeLinecap="round"
      />
    </g>
  );

  return (
    <div className="flex flex-col items-center justify-center p-8">
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="overflow-visible"
        >
          <defs>
            <filter
              id="dropShadow"
              x="-50%"
              y="-50%"
              width="200%"
              height="200%"
            >
              <feDropShadow
                dx={shadowOffset * 0.5}
                dy={shadowOffset}
                stdDeviation={shadowOffset}
                floodColor="rgba(0,0,0,0.2)"
              />
            </filter>
          </defs>

          {/* Clock-style tick marks */}
          {[
            { value: 100, angle: 0 },
            { value: 25, angle: 90 },
            { value: 50, angle: 180 },
            { value: 75, angle: 270 },
          ].map(({ value, angle }) => {
            const tickRadius = baseRadius + dialStroke / 2;
            const tickStart = polarToCartesian(
              centerX,
              centerY,
              tickRadius,
              angle
            );
            const tickEnd = polarToCartesian(
              centerX,
              centerY,
              tickRadius + size * 0.08,
              angle
            );
            const labelPos = polarToCartesian(
              centerX,
              centerY,
              tickRadius + size * 0.18,
              angle
            );

            return (
              <g key={value}>
                <line
                  x1={tickStart.x}
                  y1={tickStart.y}
                  x2={tickEnd.x}
                  y2={tickEnd.y}
                  stroke="#374151"
                  strokeWidth={size * 0.025}
                  strokeLinecap="round"
                  opacity={0.8}
                />
                <text
                  x={labelPos.x}
                  y={labelPos.y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="#374151"
                  fontSize={size * 0.12}
                  fontWeight="600"
                  fontFamily="system-ui, -apple-system, BlinkMacSystemFont, sans-serif"
                  opacity={0.85}
                >
                  {value}
                </text>
              </g>
            );
          })}

          {/* Minor tick marks */}
          {[12.5, 37.5, 62.5, 87.5].map((value) => {
            const angle = getAngle(value);
            const tickRadius = baseRadius + dialStroke / 2;
            const tickStart = polarToCartesian(
              centerX,
              centerY,
              tickRadius,
              angle
            );
            const tickEnd = polarToCartesian(
              centerX,
              centerY,
              tickRadius + size * 0.05,
              angle
            );

            return (
              <line
                key={value}
                x1={tickStart.x}
                y1={tickStart.y}
                x2={tickEnd.x}
                y2={tickEnd.y}
                stroke="#9ca3af"
                strokeWidth={size * 0.015}
                strokeLinecap="round"
                opacity={0.6}
              />
            );
          })}

          {/* Color-coded dial segments */}
          <path
            d={createArcPath(centerX, centerY, baseRadius, 0, getAngle(25))}
            fill="none"
            stroke="#ef4444"
            strokeWidth={dialStroke}
            strokeLinecap="butt"
            opacity={0.5}
          />
          <path
            d={createArcPath(
              centerX,
              centerY,
              baseRadius,
              getAngle(25),
              getAngle(50)
            )}
            fill="none"
            stroke="#eab308"
            strokeWidth={dialStroke}
            strokeLinecap="butt"
            opacity={0.5}
          />
          <path
            d={createArcPath(
              centerX,
              centerY,
              baseRadius,
              getAngle(50),
              getAngle(75)
            )}
            fill="none"
            stroke="#84cc16"
            strokeWidth={dialStroke}
            strokeLinecap="butt"
            opacity={0.5}
          />
          <path
            d={createArcPath(centerX, centerY, baseRadius, getAngle(75), 360)}
            fill="none"
            stroke="#10b981"
            strokeWidth={dialStroke}
            strokeLinecap="butt"
            opacity={0.5}
          />

          {/* Render needles with stacked icons */}
          {arrangedNeedles.map((needle) => (
            <g key={needle.id} filter="url(#dropShadow)">
              <line
                x1={centerX}
                y1={centerY}
                x2={needle.position.x}
                y2={needle.position.y}
                stroke={needle.color}
                strokeWidth={needleStrokeWidth}
                strokeLinecap="round"
              />
              {needle.icon === "star" &&
                renderStarIcon(
                  needle.position.x,
                  needle.position.y,
                  needle.color,
                  needle.scaleFactor
                )}
              {needle.icon === "user" &&
                renderUserIcon(
                  needle.position.x,
                  needle.position.y,
                  needle.color,
                  needle.scaleFactor
                )}
              {needle.icon === "dollar" &&
                renderDollarIcon(
                  needle.position.x,
                  needle.position.y,
                  needle.color,
                  needle.scaleFactor
                )}
            </g>
          ))}

          {/* Center hub */}
          <circle
            cx={centerX}
            cy={centerY}
            r={centerHubRadius}
            fill="white"
            stroke="#e2e8f0"
            strokeWidth={iconStrokeWidth * 0.83}
          />
          <circle
            cx={centerX}
            cy={centerY}
            r={centerDotRadius}
            fill="#7b189f"
          />
        </svg>
      </div>

      {/* Legend */}
      <div className="mt-16 flex gap-6 justify-center">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-purple-700"></div>
          <span className="text-sm font-medium">Quality: {qualityRating}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-purple-700"></div>
          <span className="text-sm font-medium">Service: {serviceRating}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-purple-700"></div>
          <span className="text-sm font-medium">Price: {priceRating}</span>
        </div>
      </div>
    </div>
  );
};

export default RatingGauge;
